import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import { mergeAttributes } from '@tiptap/core';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { useEffect, useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { VoiceInputButton } from "@/components/VoiceInputButton";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Link as LinkIcon,
  Highlighter,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Minus,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight
} from "lucide-react";

// Bild-Extension mit Breite + Ausrichtung (Skalieren/Zentrieren im Editor)
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.style.width || el.getAttribute("width") || null,
      },
      align: {
        default: "center",
        parseHTML: (el) => el.getAttribute("data-align") || "center",
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const { width, align, ...rest } = HTMLAttributes;
    const styles = ["height:auto"];
    if (width) styles.push(`width:${width}`);
    styles.push("display:block");
    if (align === "center") { styles.push("margin-left:auto", "margin-right:auto"); }
    else if (align === "right") { styles.push("margin-left:auto", "margin-right:0"); }
    else { styles.push("margin-left:0", "margin-right:auto"); }
    return ["img", mergeAttributes(this.options.HTMLAttributes, rest, { style: styles.join(";"), "data-align": align || "center" })];
  },
});

const MenuBar = ({ editor, onImageButton }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('URL eingeben:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b bg-muted/30 rounded-t-md">
      {editor.isActive('image') && (
        <>
          <span className="text-xs font-medium self-center text-primary px-1">Bild:</span>
          {['25%', '50%', '75%', '100%'].map((w) => (
            <Button
              key={w}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().updateAttributes('image', { width: w }).run()}
              className={`h-8 px-2 text-xs ${editor.getAttributes('image').width === w ? 'bg-accent' : ''}`}
              title={`Breite ${w}`}
              data-testid={`image-width-${w.replace('%','')}`}
            >
              {w}
            </Button>
          ))}
          <div className="w-px h-6 bg-border mx-1 self-center" />
          {[['left', AlignLeft, 'Linksbündig'], ['center', AlignCenter, 'Zentriert'], ['right', AlignRight, 'Rechtsbündig']].map(([val, Icon, label]) => (
            <Button
              key={val}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().updateAttributes('image', { align: val }).run()}
              className={`h-8 w-8 p-0 ${(editor.getAttributes('image').align || 'center') === val ? 'bg-accent' : ''}`}
              title={label}
              data-testid={`image-align-${val}`}
            >
              <Icon className="w-4 h-4" />
            </Button>
          ))}
          <div className="w-px h-6 bg-border mx-1 self-center" />
        </>
      )}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('bold') ? 'bg-accent' : ''}`}
        title="Fett"
      >
        <Bold className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('italic') ? 'bg-accent' : ''}`}
        title="Kursiv"
      >
        <Italic className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('underline') ? 'bg-accent' : ''}`}
        title="Unterstrichen"
      >
        <UnderlineIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('strike') ? 'bg-accent' : ''}`}
        title="Durchgestrichen"
      >
        <Strikethrough className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-border mx-1 self-center" />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 1 }) ? 'bg-accent' : ''}`}
        title="Überschrift 1"
      >
        <Heading1 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 2 }) ? 'bg-accent' : ''}`}
        title="Überschrift 2"
      >
        <Heading2 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 3 }) ? 'bg-accent' : ''}`}
        title="Überschrift 3"
      >
        <Heading3 className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-border mx-1 self-center" />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('bulletList') ? 'bg-accent' : ''}`}
        title="Aufzählung"
      >
        <List className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('orderedList') ? 'bg-accent' : ''}`}
        title="Nummerierte Liste"
      >
        <ListOrdered className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('blockquote') ? 'bg-accent' : ''}`}
        title="Zitat"
      >
        <Quote className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('codeBlock') ? 'bg-accent' : ''}`}
        title="Code-Block"
      >
        <Code className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        className="h-8 w-8 p-0"
        title="Trennlinie"
      >
        <Minus className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-border mx-1 self-center" />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('highlight') ? 'bg-accent' : ''}`}
        title="Hervorheben"
      >
        <Highlighter className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={editor.isActive('link') ? removeLink : addLink}
        className={`h-8 w-8 p-0 ${editor.isActive('link') ? 'bg-accent' : ''}`}
        title="Link"
      >
        <LinkIcon className="w-4 h-4" />
      </Button>
      {onImageButton && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onImageButton}
          className="h-8 w-8 p-0"
          title="Bild einfügen"
          data-testid="tiptap-image-btn"
        >
          <ImageIcon className="w-4 h-4" />
        </Button>
      )}
      
      <div className="w-px h-6 bg-border mx-1 self-center" />

      <VoiceInputButton
        onTranscript={(text) => editor.chain().focus().insertContent(text + " ").run()}
        className="h-8 w-8 p-0"
        title="Diktieren (Spracheingabe)"
      />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className="h-8 w-8 p-0"
        title="Rückgängig"
      >
        <Undo className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className="h-8 w-8 p-0"
        title="Wiederholen"
      >
        <Redo className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default function TiptapEditor({ content, onChange, placeholder = "Inhalt eingeben...", onImageUpload }) {
  const uploaderRef = useRef(onImageUpload);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  useEffect(() => { uploaderRef.current = onImageUpload; }, [onImageUpload]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      Highlight.configure({
        multicolor: false,
      }),
      CustomImage.configure({
        inline: false,
        HTMLAttributes: { class: 'rounded-md max-w-full h-auto my-2' },
      }),
      TextStyle,
      Color,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none p-4 min-h-[200px] focus:outline-none',
      },
      handlePaste: (view, event) => {
        const up = uploaderRef.current;
        if (!up) return false;
        const items = Array.from(event.clipboardData?.items || []);
        const imgItem = items.find((it) => it.type && it.type.startsWith("image/"));
        if (imgItem) {
          const file = imgItem.getAsFile();
          if (file) {
            event.preventDefault();
            up(file).then((src) => { if (src) editorRef.current?.chain().focus().setImage({ src }).run(); }).catch(() => {});
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        // Datei-Drops werden am Container behandelt (siehe onDrop unten). Web-Bild-URL als Fallback:
        const up = uploaderRef.current;
        if (!up) return false;
        if ((event.dataTransfer?.files || []).length) return false;
        const uri = event.dataTransfer?.getData("text/uri-list") || event.dataTransfer?.getData("text/plain");
        if (uri && /^https?:\/\/.+\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(uri.trim())) {
          event.preventDefault();
          const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
          const pos = coords ? coords.pos : view.state.selection.from;
          const node = view.state.schema.nodes.image.create({ src: uri.trim() });
          view.dispatch(view.state.tr.insert(pos, node));
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => { editorRef.current = editor; }, [editor]);

  const handleImageButton = () => fileInputRef.current?.click();
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploaderRef.current) return;
    try {
      const src = await uploaderRef.current(file);
      if (src) editor?.chain().focus().setImage({ src }).run();
    } catch { /* Fehler wird vom Aufrufer gemeldet */ }
  };

  // Update content when prop changes (for editing existing articles)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  const [dragActive, setDragActive] = useState(false);
  const handleContainerDragOver = (e) => {
    if (!uploaderRef.current) return;
    if (Array.from(e.dataTransfer?.types || []).includes("Files")) {
      e.preventDefault();
      if (!dragActive) setDragActive(true);
    }
  };
  const handleContainerDragLeave = (e) => {
    if (e.currentTarget === e.target) setDragActive(false);
  };
  const handleContainerDrop = async (e) => {
    const up = uploaderRef.current;
    if (!up) return;
    const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type && f.type.startsWith("image/"));
    if (!files.length) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    for (const file of files) {
      try {
        const src = await up(file);
        if (src) editor?.chain().focus().setImage({ src }).run();
      } catch { /* Fehler wird vom Aufrufer gemeldet */ }
    }
  };

  return (
    <div
      className={`border rounded-md overflow-hidden relative ${dragActive ? "ring-2 ring-primary" : ""}`}
      data-testid="tiptap-editor"
      onDragOver={handleContainerDragOver}
      onDragLeave={handleContainerDragLeave}
      onDrop={handleContainerDrop}
    >
      <MenuBar editor={editor} onImageButton={onImageUpload ? handleImageButton : undefined} />
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} data-testid="tiptap-image-input" />
      <EditorContent editor={editor} />
      {dragActive && onImageUpload && (
        <div className="absolute inset-0 bg-primary/5 border-2 border-dashed border-primary rounded-md flex items-center justify-center pointer-events-none z-10">
          <span className="text-sm font-medium text-primary bg-background/90 px-3 py-1.5 rounded-md shadow">Bild hier ablegen …</span>
        </div>
      )}
    </div>
  );
}
